import React, {
  createContext,
  useContext,
  PropsWithChildren,
  Dispatch,
  useCallback,
  useEffect,
} from 'react'

import { useReducer } from 'reinspect'

import { BridgeService } from '@/services'
import { HomeSkeleton } from '@/pages/Home/components'

import reducer, { INITIAL_STATE } from './reducer'
import { RootActions, RootState } from './reducer/types'
import { setUser } from './reducer/actions'

export type GlobalStateContextData = [RootState, Dispatch<RootActions>]

const GlobalStateContext = createContext<GlobalStateContextData | null>(null)

export const GlobalStateProvider = ({ children }: PropsWithChildren<unknown>) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE, (store) => store, 'globalStore')

  const getUserInfo = useCallback(async () => {
    if (state.user) return
    const response = await BridgeService.getUserInfo()

    dispatch(
      setUser({
        user: {
          ...response,
          cpf: response.cpf,
        },
      }),
    )
  }, [dispatch, state.user])

  const onMount = useCallback(() => {
    const callStack = async () => {
      await getUserInfo()
    }
    // Timeout used because MSW not initialized
    BridgeService.inApp ? callStack() : setTimeout(callStack, 1_000)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    onMount()
  }, [onMount])

  if (!state.user) {
    return <HomeSkeleton />
  }
  return (
    <GlobalStateContext.Provider value={[state, dispatch]}>{children}</GlobalStateContext.Provider>
  )
}

export const GlobalStateProviderTest = ({
  children,
  initialState,
}: {
  initialState: RootState
} & PropsWithChildren<unknown>) => {
  const [state, dispatch] = useReducer(reducer, initialState, (store) => store, 'globalStore')

  return (
    <GlobalStateContext.Provider value={[state, dispatch]}>{children}</GlobalStateContext.Provider>
  )
}
export const useGlobalState = () => {
  const context = useContext(GlobalStateContext)

  if (!context) {
    throw new Error(`useGlobalState must be used within a GlobalStateProvider`)
  }

  return context
}

export default useGlobalState
